import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './GroupedFlatTableView.css';
import './NewProjectTaskButton.css';
import { useAuth } from '@/features/auth/AuthContext';
import { GlobalContext } from '@/app/providers/globalState';
import { DeleteProjectModal } from './DeleteProjectModal.jsx';
import { UsecasesBulkActionsBar } from './UsecasesBulkActionsBar.jsx';
import { ProjectOverflowMenu } from './ProjectOverflowMenu.jsx';
import { resolveProjectDeleteScope } from '@/features/tasks/utils/projectDeleteScope';
import { getMoveDestinations } from '@/features/tasks/utils/bulkMoveTasks';
import { DataTable, Button, TooltipTrigger, SparkIcon, Checkbox, TruncatedText, PopoverShell } from '@spark';
import { useTaskActions } from './task/useTaskActions';
import { TaskStatusSummary, StatusWithDot } from './task/TaskStatusSummary.jsx';
import { TaskDeploymentBadge } from './task/TaskDeploymentBadge.jsx';
import { deriveInitialTaskAction } from '@/features/tasks/utils/deriveInitialTaskAction';
import { taskUrlForUsecase } from '@/features/tasks/utils/navigation';
import { DateTimeWithTimeTooltip } from '@/shared/utils/helpers';
import { getTaskTypeLabel } from '@/shared/domain/taskTypes';
import { getTaskTypeShortLabel } from '@/features/tasks/utils/types';
import {
    countByLabel,
    getCountedLabels,
    getCreatedByLabel,
    getCurrentVersionLabel,
    getDeploymentBadge,
    getLanguagesText,
    getLatestUpdatedAt,
    getStatusBreakdown,
    getTaskDescription,
    getTaskTitle,
    haveSameDescription,
} from './task/taskSummary';

const NAME_COL_MAX_WIDTH = 320;

// ── TaskActionsDropdown ──────────────────────────────────────────────────────
// Converts antd-format menu items from useTaskActions into a Spark-based
// dropdown with support for nested submenus.

function TaskActionsDropdown({ items, onOpenChange }) {
    const [open, setOpen] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState({});
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handleOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setExpandedKeys({});
                onOpenChange?.(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open, onOpenChange]);

    const handleToggle = (e) => {
        e.stopPropagation();
        const next = !open;
        setOpen(next);
        if (!next) setExpandedKeys({});
        onOpenChange?.(next);
    };

    const handleItemClick = (item) => {
        if (item.disabled) return;
        if (item.children?.length) {
            setExpandedKeys(prev => ({ ...prev, [item.key]: !prev[item.key] }));
            return;
        }
        item.onClick?.();
        setOpen(false);
        setExpandedKeys({});
        onOpenChange?.(false);
    };

    const renderItems = (menuItems, level = 0) =>
        menuItems.map((item) => {
            if (item.type === 'divider') {
                return (
                    <div
                        key={`divider-${item.key || Math.random()}`}
                        style={{
                            height: 1,
                            background: 'var(--semantic-border-default)',
                            margin: '4px 0',
                        }}
                    />
                );
            }

            const hasChildren = item.children?.length > 0;
            const isExpanded = !!expandedKeys[item.key];
            const paddingLeft = 12 + level * 12;

            return (
                <React.Fragment key={item.key}>
                    <div
                        role="menuitem"
                        tabIndex={0}
                        aria-disabled={item.disabled || undefined}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: `6px ${paddingLeft}px`,
                            cursor: item.disabled ? 'default' : 'pointer',
                            color: item.danger
                                ? 'var(--semantic-feedback-critical-on-surface)'
                                : item.disabled
                                    ? 'var(--semantic-content-tertiary)'
                                    : 'var(--semantic-content-primary)',
                            fontSize: 'var(--typography-font-size-base)',
                            lineHeight: 'var(--typography-line-height-base)',
                            fontFamily: 'var(--font-family-platform, Figtree)',
                            borderRadius: 4,
                            transition: 'background 0.1s',
                            userSelect: 'none',
                        }}
                        onClick={() => handleItemClick(item)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleItemClick(item); }}
                        onMouseEnter={(e) => {
                            if (!item.disabled) {
                                e.currentTarget.style.background = 'var(--semantic-action-secondary-bg-hover)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '';
                        }}
                    >
                        <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
                        {hasChildren && (
                            <SparkIcon
                                slug={isExpanded ? 'chevron-down' : 'chevron-right'}
                                size="small"
                            />
                        )}
                    </div>
                    {hasChildren && isExpanded && renderItems(item.children, level + 1)}
                </React.Fragment>
            );
        });

    if (!items || items.length === 0) return null;

    return (
        <div ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
            <Button
                variant="ghost"
                size="small"
                className="fs-unmask"
                aria-label="Task actions"
                icon={<SparkIcon slug="dots" size="small" />}
                onClick={handleToggle}
            />
            {open && (
                <div
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: 'calc(100% + 4px)',
                        zIndex: 1400,
                        minWidth: 200,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <PopoverShell tip={false}>
                        <div role="menu" style={{ padding: '4px 0' }}>
                            {renderItems(items)}
                        </div>
                    </PopoverShell>
                </div>
            )}
        </div>
    );
}

// ── Action wrapper components ────────────────────────────────────────────────
// These allow useTaskActions (a hook) to be called per-row inside render fns.

const TaskActionsMenu = ({ subGroup, onDeleteUsecase, onBulkDeleteUsecases, onModelDeployment, onDuplicateUsecase, onEditTask }) => {
    const { taskMenuItems, onMenuOpenChange } = useTaskActions({
        subGroup, onDeleteUsecase, onBulkDeleteUsecases, onModelDeployment, onDuplicateUsecase, onEditTask,
    });
    return <TaskActionsDropdown items={taskMenuItems} onOpenChange={onMenuOpenChange} />;
};

const SubtaskActionsMenu = ({ subGroup, usecase, onDeleteUsecase, onBulkDeleteUsecases, onModelDeployment, onDuplicateUsecase, onEditTask }) => {
    const { getSubtaskMenuItems, onMenuOpenChange } = useTaskActions({
        subGroup, onDeleteUsecase, onBulkDeleteUsecases, onModelDeployment, onDuplicateUsecase, onEditTask,
    });
    const items = getSubtaskMenuItems(usecase);
    if (items.length === 0) return null;
    return (
        <TaskActionsDropdown
            items={items}
            onOpenChange={(isOpen) => { if (isOpen) onMenuOpenChange(true); }}
        />
    );
};

// ── Data flattening ──────────────────────────────────────────────────────────

function flattenGroupTasks(project, expandedTasks) {
    const rows = [];
    for (const subGroup of project.sub_groups || []) {
        const usecases = subGroup.usecases || [];
        if (usecases.length === 0) continue;

        const isMultiObjective = usecases.length > 1;
        const taskKey = `${project.group_name}-${subGroup.sub_group_name || usecases[0]?._id}`;

        rows.push({
            _rowType: 'task',
            _rowKey: `task-${taskKey}`,
            _taskKey: taskKey,
            _subGroup: subGroup,
            _usecases: usecases,
            _isMultiObjective: isMultiObjective,
            _groupName: project.group_name,
        });

        if (isMultiObjective && expandedTasks[taskKey]) {
            for (const usecase of usecases) {
                rows.push({
                    _rowType: 'subtask',
                    _rowKey: `subtask-${usecase._id}`,
                    _subGroup: subGroup,
                    _usecase: usecase,
                    _groupName: project.group_name,
                });
            }
        }
    }
    return rows;
}

// ── Main component ───────────────────────────────────────────────────────────

const GroupedFlatTableView = ({
    groupedProjects,
    allApps,
    onDeleteUsecase,
    onBulkDeleteUsecases,
    onBulkMoveUsecases,
    onModelDeployment,
    onDuplicateUsecase,
    onEditProject,
    onDeleteProject,
    onNewTask,
    onEditTask,
}) => {
    const navigate = useNavigate();
    const { role } = useAuth();
    const { setCurrentUsecaseId, evaluationResultsReady } = useContext(GlobalContext);

    const [expandedGroups, setExpandedGroups] = useState({});
    const [allExpanded, setAllExpanded] = useState(true);
    const [expandedTasks, setExpandedTasks] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState(null);
    const projectToDeleteScope = resolveProjectDeleteScope(projectToDelete, allApps);

    const showCheckboxColumn = role !== 'Viewer';

    // ── Selection state ──────────────────────────────────────────────────────

    const allUsecases = useMemo(() => {
        const usecases = [];
        groupedProjects.forEach(project => {
            (project.sub_groups || []).forEach(subGroup => {
                (subGroup.usecases || []).forEach(usecase => {
                    if (usecase && usecase._id) {
                        usecases.push({ ...usecase, _groupName: project.group_name });
                    }
                });
            });
        });
        return usecases;
    }, [groupedProjects]);

    const allUsecaseIds = useMemo(() => allUsecases.map(u => u._id), [allUsecases]);
    const [selectedUsecaseIds, setSelectedUsecaseIds] = useState([]);

    useEffect(() => {
        setSelectedUsecaseIds(prev => prev.filter(id => allUsecaseIds.includes(id)));
    }, [allUsecaseIds]);

    const selectedUsecases = useMemo(
        () => allUsecases.filter(u => selectedUsecaseIds.includes(u._id)),
        [allUsecases, selectedUsecaseIds]
    );

    const isSelectionMode = showCheckboxColumn && selectedUsecaseIds.length > 0;

    const handleCheckboxChange = useCallback((usecaseId, checked) => {
        if (checked) {
            setSelectedUsecaseIds(prev => (prev.includes(usecaseId) ? prev : [...prev, usecaseId]));
        } else {
            setSelectedUsecaseIds(prev => prev.filter(id => id !== usecaseId));
        }
    }, []);

    const handleSelectAll = useCallback(() => {
        setSelectedUsecaseIds(allUsecaseIds);
    }, [allUsecaseIds]);

    const handleDeselectAll = useCallback(() => {
        setSelectedUsecaseIds([]);
    }, []);

    const handleBulkDelete = useCallback(async () => {
        if (selectedUsecases.length === 0) return;

        if (onBulkDeleteUsecases) {
            const res = await onBulkDeleteUsecases(selectedUsecases);
            if (res?.success) {
                setSelectedUsecaseIds([]);
            }
            return;
        }

        if (!onDeleteUsecase) return;
        try {
            for (const u of selectedUsecases) {
                // eslint-disable-next-line no-await-in-loop
                await onDeleteUsecase(u.client_id, u._id);
            }
            setSelectedUsecaseIds([]);
        } catch (e) {
            console.error(e);
        }
    }, [onDeleteUsecase, onBulkDeleteUsecases, selectedUsecases]);

    const moveDestinations = useMemo(
        () => getMoveDestinations(
            allApps,
            selectedUsecases.map((u) => u.group_name || u._groupName)
        ),
        [allApps, selectedUsecases]
    );

    const handleBulkMove = useCallback(async (destName) => {
        if (!onBulkMoveUsecases || selectedUsecases.length === 0) return { success: false };
        const res = await onBulkMoveUsecases(selectedUsecases, destName);
        if (res?.success) setSelectedUsecaseIds([]);
        return res;
    }, [onBulkMoveUsecases, selectedUsecases]);

    // ── Group expand / collapse ──────────────────────────────────────────────

    useEffect(() => {
        setExpandedGroups(prev => {
            const nextState = { ...prev };
            groupedProjects.forEach(p => {
                if (!(p.group_name in nextState)) {
                    nextState[p.group_name] = true;
                }
            });
            Object.keys(nextState).forEach(name => {
                if (!groupedProjects.find(p => p.group_name === name)) {
                    delete nextState[name];
                }
            });
            return nextState;
        });
    }, [groupedProjects]);

    const toggleAllGroups = useCallback(() => {
        const newExpanded = !allExpanded;
        setAllExpanded(newExpanded);
        const newState = {};
        groupedProjects.forEach(p => { newState[p.group_name] = newExpanded; });
        setExpandedGroups(newState);
    }, [allExpanded, groupedProjects]);

    const toggleGroup = useCallback((groupName) => {
        setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
    }, []);

    useEffect(() => {
        const expandListener = (e) => {
            const projectName = e?.detail?.projectName;
            if (!projectName) return;
            setExpandedGroups(prev => ({ ...prev, [projectName]: true }));
        };
        window.addEventListener('home:expand-project', expandListener);
        return () => window.removeEventListener('home:expand-project', expandListener);
    }, []);

    // ── Composed-task expand / collapse ──────────────────────────────────────

    const toggleTask = useCallback((taskKey) => {
        setExpandedTasks(prev => ({ ...prev, [taskKey]: !prev[taskKey] }));
    }, []);

    // ── Navigation ───────────────────────────────────────────────────────────

    const navigateToUsecase = useCallback((usecase, isMultiObjective) => {
        const action = deriveInitialTaskAction(usecase, {
            isMultiObjective,
            evaluationResultsReady,
        });
        const url = taskUrlForUsecase(usecase, {
            objective: usecase?._id,
            action,
        });
        if (!url) return;
        if (usecase?._id) setCurrentUsecaseId(usecase._id);
        navigate(url);
    }, [navigate, setCurrentUsecaseId, evaluationResultsReady]);

    // ── Helpers ──────────────────────────────────────────────────────────────

    const projectTaskCount = (project) =>
        (project.sub_groups || []).reduce(
            (acc, sg) => acc + (sg.usecases?.length || 0),
            0
        );

    const headerCheckboxStatus =
        selectedUsecaseIds.length === 0 ? 'inactive' :
        selectedUsecaseIds.length === allUsecaseIds.length ? 'active' :
        'indeterminate';

    const handleHeaderCheckboxChange = () => {
        if (selectedUsecaseIds.length === 0) {
            handleSelectAll();
        } else {
            handleDeselectAll();
        }
    };

    // ── DataTable columns ────────────────────────────────────────────────────

    const columns = useMemo(() => {
        const cols = [
            {
                key: 'spacer',
                width: 48,
                title: (
                    <Button
                        variant="ghost"
                        size="small"
                        className="spark-table-expand-toggle-btn"
                        aria-expanded={allExpanded}
                        aria-label={allExpanded ? 'Collapse all rows' : 'Expand all rows'}
                        onClick={toggleAllGroups}
                        icon={
                            <SparkIcon
                                slug={allExpanded ? 'chevron-down-large' : 'chevron-right-large'}
                                size="small"
                            />
                        }
                    />
                ),
                render: (_, record) => {
                    if (record._rowType === 'task' && record._isMultiObjective) {
                        const isExpanded = !!expandedTasks[record._taskKey];
                        return (
                            <TooltipTrigger text={isExpanded ? 'Collapse subtasks' : 'Expand subtasks'}>
                                <Button
                                    variant="ghost"
                                    size="small"
                                    aria-expanded={isExpanded}
                                    aria-label={isExpanded ? 'Collapse subtasks' : 'Expand subtasks'}
                                    onClick={(e) => { e.stopPropagation(); toggleTask(record._taskKey); }}
                                    icon={
                                        <SparkIcon
                                            slug={isExpanded ? 'chevron-down-large' : 'chevron-right-large'}
                                            size="small"
                                        />
                                    }
                                    style={{ color: 'var(--semantic-action-primary-bg-default)' }}
                                />
                            </TooltipTrigger>
                        );
                    }
                    return null;
                },
            },
            {
                key: 'name',
                dataIndex: '_rowKey',
                title: 'Task name',
                flex: 2,
                minWidth: 200,
                render: (_, record) => {
                    if (record._rowType === 'task') {
                        const subGroup = record._subGroup;
                        const usecases = record._usecases;
                        const isMultiObjective = record._isMultiObjective;
                        const firstUsecase = usecases[0];
                        const cardTitle = getTaskTitle(subGroup);
                        const cardDescription = getTaskDescription(subGroup);
                        const sameDescriptionAcrossUsecases = haveSameDescription(usecases);
                        const showDescriptionOnTaskRow = !isMultiObjective || sameDescriptionAcrossUsecases;
                        const deploymentBadge = getDeploymentBadge(usecases);
                        const languagesText = getLanguagesText(usecases);
                        const taskNameMaxWidth = !deploymentBadge
                            ? NAME_COL_MAX_WIDTH
                            : deploymentBadge.breakdown
                                ? NAME_COL_MAX_WIDTH - 180
                                : NAME_COL_MAX_WIDTH - 140;

                        return (
                            <div style={{ minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                    <TruncatedText
                                        text={cardTitle}
                                        maxWidth={taskNameMaxWidth}
                                        style={{ fontWeight: 600, fontSize: 15, color: '#1A1A1A' }}
                                        onClick={() => navigateToUsecase(firstUsecase, isMultiObjective)}
                                        tooltipText={cardTitle}
                                    />
                                    <TaskDeploymentBadge badge={deploymentBadge} />
                                </div>
                                {cardDescription && showDescriptionOnTaskRow && (
                                    <TruncatedText
                                        text={cardDescription}
                                        tooltipText={cardDescription}
                                        maxWidth={NAME_COL_MAX_WIDTH}
                                        style={{ color: '#6B7280', fontSize: 13, marginTop: 2 }}
                                    />
                                )}
                                {languagesText && (
                                    <TruncatedText
                                        text={`Languages: ${languagesText}`}
                                        tooltipText={`Languages: ${languagesText}`}
                                        maxWidth={NAME_COL_MAX_WIDTH}
                                        style={{ color: '#6B7280', fontSize: 12, marginTop: 2 }}
                                    />
                                )}
                            </div>
                        );
                    }

                    if (record._rowType === 'subtask') {
                        const usecase = record._usecase;
                        const subGroup = record._subGroup;
                        const usecases = subGroup?.usecases || [];
                        const showDescriptionOnSubtaskRow = usecases.length > 1 && !haveSameDescription(usecases);

                        return (
                            <div style={{ paddingLeft: 24, minWidth: 0 }}>
                                <TruncatedText
                                    text={usecase.usecase_name || 'Sub-task'}
                                    maxWidth={NAME_COL_MAX_WIDTH}
                                    onClick={() => navigateToUsecase(usecase, true)}
                                />
                                {usecase.usecase_goal && showDescriptionOnSubtaskRow && (
                                    <TruncatedText
                                        text={usecase.usecase_goal}
                                        tooltipText={usecase.usecase_goal}
                                        maxWidth={NAME_COL_MAX_WIDTH}
                                        style={{ color: '#888', fontSize: 13, marginTop: 2 }}
                                    />
                                )}
                            </div>
                        );
                    }

                    return null;
                },
            },
        ];

        if (showCheckboxColumn) {
            cols.unshift({
                key: 'checkbox',
                width: 48,
                title: (
                    <Checkbox
                        label=""
                        status={headerCheckboxStatus}
                        onChange={handleHeaderCheckboxChange}
                    />
                ),
                render: (_, record) => {
                    if (record._rowType === 'task') {
                        const usecaseIds = record._usecases.map(uc => uc._id);
                        const selectedCount = usecaseIds.filter(id => selectedUsecaseIds.includes(id)).length;
                        const taskAllSelected = usecaseIds.length > 0 && selectedCount === usecaseIds.length;
                        const indeterminate = selectedCount > 0 && !taskAllSelected;

                        return (
                            <div onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                    label=""
                                    status={taskAllSelected ? 'active' : indeterminate ? 'indeterminate' : 'inactive'}
                                    onChange={() => {
                                        const next = !taskAllSelected;
                                        usecaseIds.forEach(id => handleCheckboxChange(id, next));
                                    }}
                                />
                            </div>
                        );
                    }

                    if (record._rowType === 'subtask') {
                        return (
                            <div onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                    label=""
                                    status={selectedUsecaseIds.includes(record._usecase._id) ? 'active' : 'inactive'}
                                    onChange={() => handleCheckboxChange(record._usecase._id, !selectedUsecaseIds.includes(record._usecase._id))}
                                />
                            </div>
                        );
                    }

                    return null;
                },
            });
        }

        cols.push(
            {
                key: 'version',
                title: 'Version',
                flex: 1,
                render: (_, record) => {
                    if (record._rowType === 'task') {
                        return getCurrentVersionLabel(record._usecases) || '-';
                    }
                    if (record._rowType === 'subtask') {
                        return `V${record._usecase.version}.0`;
                    }
                    return null;
                },
            },
            {
                key: 'status',
                title: 'Status',
                flex: 1,
                render: (_, record) => {
                    if (record._rowType === 'task') {
                        const usecases = record._usecases;
                        const statusSummary = getCountedLabels(usecases, uc => uc?.status);
                        const statusBreakdown = getStatusBreakdown(usecases);
                        return <TaskStatusSummary statuses={statusSummary} breakdown={statusBreakdown} />;
                    }
                    if (record._rowType === 'subtask') {
                        return <StatusWithDot status={record._usecase.status} />;
                    }
                    return null;
                },
            },
            {
                key: 'task_type',
                title: 'Task Type',
                flex: 1,
                render: (_, record) => {
                    if (record._rowType === 'task') {
                        const taskTypeSummary = countByLabel(record._usecases, uc =>
                            getTaskTypeShortLabel(uc?.task_type)
                        );
                        return (
                            <TruncatedText
                                text={taskTypeSummary}
                                tooltipText={taskTypeSummary}
                                maxWidth={220}
                                style={{ fontSize: 14 }}
                            />
                        );
                    }
                    if (record._rowType === 'subtask') {
                        const uc = record._usecase;
                        return (
                            <span className="fs-unmask">
                                {getTaskTypeLabel(uc.task_type, uc.is_multilabel, uc.classify_speakers)}
                            </span>
                        );
                    }
                    return null;
                },
            },
            {
                key: 'created_by_user',
                title: 'Created by',
                flex: 1,
                render: (_, record) => {
                    if (record._rowType === 'task') {
                        const createdByName = getCreatedByLabel(record._usecases[0]) || '-';
                        return (
                            <TruncatedText
                                text={<span className="fs-unmask">{createdByName}</span>}
                                maxWidth={150}
                                style={{ fontSize: 14 }}
                            />
                        );
                    }
                    if (record._rowType === 'subtask') {
                        return (
                            <TruncatedText
                                text={<span className="fs-unmask">{getCreatedByLabel(record._usecase) || '-'}</span>}
                                maxWidth={150}
                                style={{ fontSize: 14 }}
                            />
                        );
                    }
                    return null;
                },
            },
            {
                key: 'last_update_time',
                title: 'Last updated',
                flex: 1,
                render: (_, record) => {
                    if (record._rowType === 'task') {
                        const lastUpdatedAt = getLatestUpdatedAt(record._usecases);
                        return (
                            <span className="fs-unmask">
                                <DateTimeWithTimeTooltip value={lastUpdatedAt} emptyPlaceholder="" />
                            </span>
                        );
                    }
                    if (record._rowType === 'subtask') {
                        return (
                            <span className="fs-unmask">
                                <DateTimeWithTimeTooltip value={record._usecase.last_update_time} emptyPlaceholder="" />
                            </span>
                        );
                    }
                    return null;
                },
            },
            {
                key: 'actions',
                title: '',
                width: 56,
                render: (_, record) => {
                    if (record._rowType === 'task') {
                        return (
                            <TaskActionsMenu
                                subGroup={record._subGroup}
                                onDeleteUsecase={onDeleteUsecase}
                                onBulkDeleteUsecases={onBulkDeleteUsecases}
                                onModelDeployment={onModelDeployment}
                                onDuplicateUsecase={onDuplicateUsecase}
                                onEditTask={onEditTask}
                            />
                        );
                    }
                    if (record._rowType === 'subtask') {
                        return (
                            <SubtaskActionsMenu
                                subGroup={record._subGroup}
                                usecase={record._usecase}
                                onDeleteUsecase={onDeleteUsecase}
                                onBulkDeleteUsecases={onBulkDeleteUsecases}
                                onModelDeployment={onModelDeployment}
                                onDuplicateUsecase={onDuplicateUsecase}
                                onEditTask={onEditTask}
                            />
                        );
                    }
                    return null;
                },
            },
        );

        return cols;
    }, [
        allExpanded,
        toggleAllGroups,
        expandedTasks,
        toggleTask,
        showCheckboxColumn,
        headerCheckboxStatus,
        handleHeaderCheckboxChange,
        selectedUsecaseIds,
        handleCheckboxChange,
        navigateToUsecase,
        onDeleteUsecase,
        onBulkDeleteUsecases,
        onModelDeployment,
        onDuplicateUsecase,
        onEditTask,
    ]);

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <>
            {isSelectionMode && (
                <UsecasesBulkActionsBar
                    selectedCount={selectedUsecaseIds.length}
                    totalCount={allUsecases.length}
                    onSelectAll={handleSelectAll}
                    onDeselectAll={handleDeselectAll}
                    onDelete={handleBulkDelete}
                    onMove={onBulkMoveUsecases ? handleBulkMove : undefined}
                    destinations={moveDestinations}
                    disabled={false}
                    itemNames={selectedUsecases.map((usecase) => usecase.usecase_name || 'Task')}
                />
            )}

            <div className="grouped-flat-table-wrapper">
            {/* Header-only DataTable */}
            <div className="grouped-flat-header-datatable">
                <DataTable columns={columns} dataSource={[]} showHeader emptyText="" />
            </div>

            {/* Project groups */}
            {groupedProjects.map(project => {
                const isExpanded = !!expandedGroups[project.group_name];
                const projectRows = isExpanded ? flattenGroupTasks(project, expandedTasks) : [];
                const deleteScope = resolveProjectDeleteScope(project, allApps);
                const isFilteredDelete = deleteScope.mode === 'filtered-tasks';

                return (
                    <React.Fragment key={project.group_name}>
                        {/* Expandable project group header */}
                        <div
                            className={`spark-table-row spark-table-row-expandable spark-table-row-interactive${isExpanded ? ' spark-table-row-expandable-open' : ''}`}
                            role="row"
                            id={`project-${project.group_name}`}
                            onClick={() => toggleGroup(project.group_name)}
                            style={{ cursor: 'pointer' }}
                        >
                            {showCheckboxColumn ? (
                                <div
                                    className="spark-table-cell spark-table-cell-checkbox"
                                    role="cell"
                                    aria-hidden="true"
                                />
                            ) : null}
                            <div className="spark-table-cell spark-table-cell-expand">
                                <Button
                                    variant="ghost"
                                    size="small"
                                    className="spark-table-expand-toggle-btn"
                                    aria-expanded={isExpanded}
                                    aria-label={isExpanded ? 'Collapse project' : 'Expand project'}
                                    onClick={(e) => { e.stopPropagation(); toggleGroup(project.group_name); }}
                                    icon={
                                        <SparkIcon
                                            slug={isExpanded ? 'chevron-down-large' : 'chevron-right-large'}
                                            size="small"
                                        />
                                    }
                                />
                            </div>
                            <div className="spark-table-cell spark-table-cell-expand-parent">
                                <div className="spark-table-cell-two-lines-content">
                                    <div className="spark-table-cell-title-line">
                                        <p
                                            className="spark-table-cell-title-text"
                                            style={{ color: '#000624' }}
                                        >
                                            {project.group_name}
                                        </p>
                                        <p className="spark-table-cell-title-count fs-unmask">
                                            ({projectTaskCount(project)})
                                        </p>
                                    </div>
                                    {project.project_goal && (
                                        <p className="spark-table-cell-subtitle-text">
                                            {project.project_goal}
                                        </p>
                                    )}
                                </div>
                                {role !== 'Viewer' && (
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <ProjectOverflowMenu
                                            deleteLabel={isFilteredDelete ? 'Delete filtered tasks' : 'Delete Project'}
                                            onEdit={() => onEditProject && onEditProject({
                                                group_name: project.group_name,
                                                project_goal: project.project_goal,
                                            })}
                                            onDelete={() => {
                                                setProjectToDelete(project);
                                                setShowDeleteModal(true);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            <div
                                className="spark-table-cell spark-table-cell-text-button"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {role === 'Viewer' ? (
                                    <TooltipTrigger text="Viewers cannot create new project tasks">
                                        <Button
                                            className="fs-unmask new-project-task-btn"
                                            variant="ghost"
                                            size="tiny"
                                            disabled
                                        >
                                            Add new task
                                        </Button>
                                    </TooltipTrigger>
                                ) : (
                                    <Button
                                        className="fs-unmask new-project-task-btn"
                                        variant="ghost"
                                        size="tiny"
                                        disableWhenReadOnly
                                        onClick={() => onNewTask && onNewTask(project.group_name)}
                                    >
                                        Add new task
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Per-project DataTable */}
                        {isExpanded && projectRows.length > 0 && (
                            <DataTable
                                columns={columns}
                                dataSource={projectRows}
                                rowKey="_rowKey"
                                loading={false}
                                showHeader={false}
                                disableHoverBackground
                            />
                        )}
                    </React.Fragment>
                );
            })}

        </div>

            <DeleteProjectModal
                visible={showDeleteModal}
                onCancel={() => { setShowDeleteModal(false); setProjectToDelete(null); }}
                onConfirm={async () => {
                    if (onDeleteProject && projectToDelete) {
                        setDeleteLoading(true);
                        try {
                            await onDeleteProject(projectToDelete);
                            setShowDeleteModal(false);
                            setProjectToDelete(null);
                        } finally {
                            setDeleteLoading(false);
                        }
                    } else {
                        setShowDeleteModal(false);
                        setProjectToDelete(null);
                    }
                }}
                projectName={projectToDelete?.group_name}
                loading={deleteLoading}
                isFilteredDelete={projectToDeleteScope.mode === 'filtered-tasks'}
                filteredTaskCount={projectToDeleteScope.visibleTaskCount}
            />
        </>
    );
};

export default GroupedFlatTableView;
